import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { api, resolveImage } from "@/lib/api";
import { ArrowLeft, ExternalLink } from "lucide-react";
import SEO from "@/components/SEO";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="blog-not-found">
        <div className="font-display text-4xl text-white mb-4">Post not found.</div>
        <Link to="/blog" className="text-[#FF3D8B] uppercase text-sm tracking-widest">Back to BreezeBlog</Link>
      </div>
    );
  }
  if (!post) return <div className="max-w-3xl mx-auto px-6 py-32 text-white/50 text-sm">Loading…</div>;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "image": post.image_url ? `https://bootybreeze.com${post.image_url}` : undefined,
    "datePublished": post.published_at,
    "author": { "@type": "Person", "name": post.author || "Bree" },
    "publisher": {
      "@type": "Organization",
      "name": "Booty Breeze",
      "logo": { "@type": "ImageObject", "url": "https://bootybreeze.com/icon-512.png" },
    },
    "mainEntityOfPage": `https://bootybreeze.com/blog/${post.slug}`,
    "keywords": (post.tags || []).join(", "),
  };

  return (
    <article className="max-w-3xl mx-auto px-6 sm:px-10 py-12" data-testid={`blog-post-${post.slug}`}>
      <SEO
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.image_url ? `https://bootybreeze.com${post.image_url}` : undefined}
        type="article"
        jsonLd={jsonLd}
      />
      <Link
        to="/blog"
        className="inline-flex items-center gap-2 text-white/50 hover:text-[#FF3D8B] text-xs uppercase tracking-widest mb-8 transition-colors"
      >
        <ArrowLeft size={14} /> BreezeBlog
      </Link>

      <div className="flex flex-wrap gap-2 mb-4">
        {(post.tags || []).map((t) => (
          <span key={t} className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/5 text-[#A855F7]">
            {t}
          </span>
        ))}
      </div>

      <h1 className="font-display text-4xl sm:text-6xl text-white leading-tight" data-testid="blog-post-title">
        {post.title}
      </h1>
      <div className="text-white/40 text-xs mt-4">
        By {post.author || "Bree"} · {(post.published_at || "").slice(0, 10)}
      </div>

      {post.image_url && (
        <img
          src={resolveImage(post.image_url)}
          alt={post.title}
          className="w-full aspect-[16/9] object-cover rounded-xl mt-8 border border-white/10"
        />
      )}

      <div className="prose prose-invert prose-pink max-w-none mt-10
        prose-headings:font-display prose-headings:text-white
        prose-h2:text-3xl prose-h2:mt-10 prose-h2:mb-3
        prose-h3:text-2xl prose-h3:text-[#FF3D8B]
        prose-p:text-white/75 prose-p:leading-relaxed
        prose-a:text-[#FF3D8B] hover:prose-a:text-[#ff5ba0]
        prose-strong:text-white
        prose-li:text-white/75
        prose-blockquote:border-l-[#A855F7] prose-blockquote:text-white/70"
        data-testid="blog-post-body"
      >
        <ReactMarkdown>{post.body_markdown}</ReactMarkdown>
      </div>

      {post.sources?.length > 0 && (
        <aside className="mt-14 pt-8 border-t border-white/10">
          <div className="text-white/40 text-xs uppercase tracking-widest mb-3">Sources</div>
          <ul className="space-y-2">
            {post.sources.map((s, i) => (
              <li key={s.url || `src-${i}`}>
                <a href={s.url} target="_blank" rel="noopener noreferrer nofollow"
                  className="inline-flex items-center gap-1.5 text-sm text-[#A855F7] hover:text-[#FF3D8B] transition-colors">
                  <ExternalLink size={12} /> {s.title || s.url}
                </a>
              </li>
            ))}
          </ul>
        </aside>
      )}

      <div className="mt-16 text-center rounded-2xl border border-[#FF3D8B]/30 bg-gradient-to-br from-[#FF3D8B]/10 to-[#A855F7]/10 p-10">
        <div className="text-[#A855F7] uppercase text-xs tracking-widest mb-2">Ready for a scent?</div>
        <h3 className="font-display text-3xl text-white">Shop the collection.</h3>
        <Link
          to="/shop"
          className="mt-6 inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-widest text-xs hover:bg-[#ff5ba0] transition-colors">
          Shop All
        </Link>
      </div>
    </article>
  );
}
